import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ColorBox } from 'devextreme-react';
import Color from 'color';

// A thin wrapper around DevExtreme ColorBox that renders preset swatches
// in the dropdown popup. It accepts `presetColors` as a prop.
// Compatible with devextreme 21.2.

function PresetColorBox(props) {
    const { presetColors = [], onPresetClickClose = true, onOpened, onClosed, onInitialized, ...rest } = props;

    const instanceRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [containerEl, setContainerEl] = useState(null);

    const hasPresets = Array.isArray(presetColors) && presetColors.length > 0;

    const ensureContainer = () => {
        if (!instanceRef.current || !instanceRef.current.content) return null;
        const contentEl = instanceRef.current.content();
        if (!contentEl) return null;
        let holder = contentEl.querySelector('.dx-colorbox-presets');
        if (!holder) {
            holder = document.createElement('div');
            holder.className = 'dx-colorbox-presets';
            // Basic spacing separation from color palette
            holder.style.marginTop = '8px';
            holder.style.paddingTop = '8px';
            holder.style.borderTop = '1px solid rgba(0,0,0,0.08)';
            contentEl.appendChild(holder);
        }
        return holder;
    };

    const handleInitialized = (e) => {
        instanceRef.current = e.component;
        if (onInitialized) onInitialized(e);
    };

    const handleOpened = (e) => {
        setIsOpen(true);
        const holder = ensureContainer();
        if (holder) setContainerEl(holder);
        if (onOpened) onOpened(e);
    };

    const handleClosed = (e) => {
        setIsOpen(false);
        // Clean up any injected container
        if (containerEl) {
            try {
                ReactDOM.unmountComponentAtNode(containerEl);
                if (containerEl.parentNode) containerEl.parentNode.removeChild(containerEl);
            } catch (_) { /* noop */ }
        }
        setContainerEl(null);
        if (onClosed) onClosed(e);
    };

    const onPresetClick = (hex) => {
        if (!instanceRef.current) return;
        try {
            instanceRef.current.option('value', hex);
            if (onPresetClickClose) instanceRef.current.close();
        } catch (_) { /* noop */ }
    };

    const presetsPortal = useMemo(() => {
        if (!isOpen || !containerEl || !hasPresets) return null;
        const wrapStyle = {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
        };

        return ReactDOM.createPortal(
            <div>
                <div style={{ marginBottom: '4px', color: 'rgba(0,0,0,0.6)', fontSize: 12 }}>{props.label}</div>
                <div style={wrapStyle}>
                    {
                        presetColors.map((hex) => {
                            const badgeColor = new Color(hex);
                            return <button
                                    onClick={() => onPresetClick(hex)}
                                    className='badge text-shadow-0'
                                    style={{
                                        fontSize: '12px',
                                        backgroundColor: badgeColor.lighten(.8),
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        borderColor: badgeColor.darken(.3),
                                        boxSizing: 'border-box',
                                        color: badgeColor.darken(.3),
                                        minWidth: "6em"
                                    }}>{hex}</button>
                            
                        })}
                </div>
            </div>,
            containerEl
        );
    }, [isOpen, containerEl, hasPresets, presetColors]);  // eslint-disable-line

    return (
        <>
            <ColorBox
                onInitialized={handleInitialized}
                onOpened={handleOpened}
                onClosed={handleClosed}
                {...rest}
            />
            {presetsPortal}
        </>
    );
}

export default PresetColorBox;